import type { StockingLineageDto } from '@/services/types';
import { getIsoWeek, getIsoWeekYear, parseISOString } from '@/utils/date';

import type { ApiWire } from '../shared/types';

import { plannedStartDateForWeek, seasonOfDay } from './plannedWeek';
import type { StockingLineage, StockingLineageBlocker } from './stockingAssignment.types';
import { suggestStockingCycleName } from './stockingCycleNaming';
import type { CreateStockingCycleInput } from './types';

const LIVE_STATUSES = new Set(['pending', 'active']);

function mapBlocker(blocker: string | null | undefined): StockingLineageBlocker | null {
    if (blocker === 'LINEAGE_SPANS_CLIENTS') return 'spansClients';
    if (blocker === 'LINEAGE_SPANS_SITES') return 'spansSites';
    return null;
}

export function mapStockingLineageToDomain(dto: ApiWire<StockingLineageDto>): StockingLineage {
    return {
        fishGroupId: dto.fishGroupId,
        groups: (dto.groups ?? []).map((group) => ({
            fishGroupId: group.fishGroupId,
            name: group.name,
            isLive: LIVE_STATUSES.has(group.status),
            stockingCycleId: group.stockingCycleId ?? null,
            startedAt: parseISOString(group.startedAt),
            requested: group.requested,
        })),
        stockingCycleIds: dto.stockingCycleIds ?? [],
        blocker: mapBlocker(dto.blocker),
        expectsSingleActiveCycle: dto.expectsSingleActiveCycle ?? true,
    };
}

/**
 * A stocking created for a lineage that is already in the water: planned for the week of its
 * earliest start, and named from that week's Monday, so the season in the name is the season
 * of the planned date. Naming the start itself disagrees when the Monday falls in the other
 * season (a July start whose week began in June, a January start whose week began in December).
 * A lineage with no start falls back to `now`.
 */
export function lineageStockingDefaults(
    lineage: StockingLineage,
    existingNames: readonly string[],
    now: Date
): CreateStockingCycleInput {
    const starts = lineage.groups
        .map((group) => group.startedAt?.getTime())
        .filter((start): start is number => start !== undefined);
    const start = starts.length > 0 ? new Date(Math.min(...starts)) : now;
    const plannedStartDate = plannedStartDateForWeek(getIsoWeekYear(start), getIsoWeek(start));
    const plannedMonday = new Date(`${plannedStartDate}T00:00:00.000Z`);
    return {
        name: suggestStockingCycleName(
            seasonOfDay(plannedMonday),
            plannedMonday.getUTCFullYear(),
            existingNames
        ),
        plannedStartDate,
    };
}
